import { LocalDate } from "@js-joda/core";
import { serializable } from "serializr";
import { localDate } from "../serialization/MobxTypes";

/**
 *  DateRange can be used to:
 *  - get all dates between a specified startDate and endDate
 *  - check if a specific date is inside a range of startDate and endDate
 *  - get a number value of days between startDate and endDate
 *  - check if a DateRange overlaps another DateRange
 */
export class DateRange {

   @serializable(localDate())
   readonly startDate: LocalDate;
   @serializable(localDate())
   readonly endDate: LocalDate;

   constructor(startDate: LocalDate = LocalDate.now(), endDate: LocalDate = startDate) {

      if (startDate === null || startDate === undefined) {
         throw Error('start date must not be null or undefined');
      }

      if (endDate === null || endDate === undefined) {
         throw Error('end date must not be null or undefined');
      }

      if (startDate.isAfter(endDate)) {
         throw Error('start date must be after the end date');
      }

      this.startDate = startDate;
      this.endDate = endDate;
   }

   static of(startDate: LocalDate, endDate: LocalDate = startDate): DateRange {
      return new DateRange(startDate, endDate);
   }

   equals(dateRange: DateRange): boolean {
      return this.startDate.equals(dateRange.startDate) && this.endDate.equals(dateRange.endDate);
   }

   contains(date: LocalDate): boolean {
      return !this.startDate.isAfter(date) && !this.endDate.isBefore(date);
   }

   overlaps(dateRange: DateRange): boolean {
      return this.startDate.compareTo(dateRange.endDate) <= 0 && this.endDate.compareTo(dateRange.startDate) >= 0;
   }

   numberOfDaysInRange(): number {
      return this.datesInRange().length;
   }

   datesInRange(): Array<LocalDate> {
      return Array.from(this);
   }

   [Symbol.iterator]() {
      let step = this.startDate.minusDays(1);
      const end = this.endDate;
      const iterator = {
         next() {
            step = step.plusDays(1);
            return { value: step, done: step.isAfter(end) };
         }
      };
      return iterator;
   }
}

/**
 * Predefined presets that can be used to get date range based on passed date. If omitted, default is today's date.
 */
export const DATE_RANGE_PRESETS = {
   LAST_YEAR: (date: LocalDate = LocalDate.now()) => new DateRange(date.withDayOfYear(1).minusDays(1).withDayOfYear(1),
                                                                   date.withDayOfYear(1).minusDays(1)),
   LAST_MONTH: (date: LocalDate = LocalDate.now()) => new DateRange(date.withDayOfMonth(1).minusDays(1).withDayOfMonth(1),
                                                                    date.withDayOfMonth(1).minusDays(1)),
   LAST_30_DAYS: (date: LocalDate = LocalDate.now()) => new DateRange(date.minusDays(29),
                                                                      date),
   LAST_7_DAYS: (date: LocalDate = LocalDate.now()) => new DateRange(date.minusDays(6),
                                                                     date),
   LAST_3_DAYS: (date: LocalDate = LocalDate.now()) => new DateRange(date.minusDays(2),
                                                                     date),
   YESTERDAY: (date: LocalDate = LocalDate.now()) => new DateRange(date.minusDays(1),
                                                                   date.minusDays(1)),
   TODAY: (date: LocalDate = LocalDate.now()) => new DateRange(date,
                                                               date),
   TOMORROW: (date: LocalDate = LocalDate.now()) => new DateRange(date.plusDays(1),
                                                                  date.plusDays(1)),
   THIS_MONTH: (date: LocalDate = LocalDate.now()) => new DateRange(date.withDayOfMonth(1),
                                                                    date.plusMonths(1).withDayOfMonth(1).minusDays(1)),
   THIS_YEAR: (date: LocalDate = LocalDate.now()) => new DateRange(date.withDayOfYear(1),
                                                                   date.plusYears(1).withDayOfYear(1).minusDays(1)),
   NEXT_3_DAYS: (date: LocalDate = LocalDate.now()) => new DateRange(date,
                                                                     date.plusDays(2)),
   NEXT_7_DAYS: (date: LocalDate = LocalDate.now()) => new DateRange(date,
                                                                     date.plusDays(6)),
   NEXT_30_DAYS: (date: LocalDate = LocalDate.now()) => new DateRange(date,
                                                                      date.plusDays(29)),
   NEXT_MONTH: (date: LocalDate = LocalDate.now()) => new DateRange(date.withDayOfMonth(1).plusMonths(1),
                                                                    date.withDayOfMonth(1).plusMonths(2).minusDays(1)),
   NEXT_YEAR: (date: LocalDate = LocalDate.now()) => new DateRange(date.withDayOfYear(1).plusYears(1),
                                                                   date.withDayOfYear(1).plusYears(2).minusDays(1))
};
